import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, InputGroup, Alert } from 'react-bootstrap';
import LoaderButton from './LoaderButton';
import { UiError } from '../libs/errorLib';
import { useAppContext } from '../libs/contextLib';
import config from '../config';
import {
  getPromiseFromApi,
} from '../libs/integralLib';

/**
 * Reusable address component with postcode lookup.
 * For an example of usage please see src/containers/RequestAccount.js around
 * line 609.
 */
function AddressPicker({
  formTitle = 'Address',
  id = 'addressPicker',
  className = '',
  handleFieldChange,
  // Props to fill values in form.
  postcode = '',
  addressLine1 = '',
  addressLine2 = '',
  addressTown = '',
  addressCounty = '',
  addressCountry = 'United Kingdom',
  // Allow override of field ids.
  postcodeFieldId = 'postcode',
  addressLine1FieldId = 'addressLine1',
  addressLine2FieldId = 'addressLine2',
  addressTownFieldId = 'addressTown',
  addressCountyFieldId = 'addressCounty',
  addressCountryFieldId = 'addressCountry',
  oldLayout = true,
  ...props
}) {
  const { setError }                                        = useAppContext();
  const [addressSearchIsLoading, setAddressSearchIsLoading] = useState(false);
  const [addressList, setAddressList]                       = useState(null);
  const [selectedAddress, setSelectedAddress]               = useState(null);

  /**
   * Set the state on fields whenever the selected address changes.
   * Calls the parent's handleFieldChange with a partialFields.
   */
  useEffect(() => {
    const selectedIndex = parseFloat(selectedAddress);
    if(!isNaN(selectedIndex) && selectedIndex < addressList.length) {
      const selectedAddress = addressList[selectedIndex];
      handleFieldChange(
        {
          partialFields: {
            [addressLine1FieldId]: selectedAddress.line1,
            [addressLine2FieldId]: selectedAddress.line2,
            [addressTownFieldId]: selectedAddress.town,
            [addressCountyFieldId]: selectedAddress.county,
          }
        }
      )
    }
  },
  // eslint-disable-next-line
  [selectedAddress]
  )

  function handleAddressSearch(event) {
    event.preventDefault();
    setAddressSearchIsLoading(true);
    // Post the data.
    getPromiseFromApi(
      `${config.api.address}/address/${encodeURIComponent(postcode)}`,
    )
    .then(
      ( { code, data } ) => {
        if(code === 200) {
          setError(
            new UiError('requestAccount', '', 'resolved')
          );
          setSelectedAddress(null);
          setAddressList(data);
        }
        else {
          setError(
            new UiError('requestAccount', data.error.message, "danger")
          );
        }
      }
    )
    .catch(
      message => {
        setError(
          new UiError('requestAccount', message)
        );
      }
    )
    .finally(
      () => {
        setAddressSearchIsLoading(false);
      }
    )
  }

  function handleSelectedAddressChange(event) {
    setSelectedAddress(event.target.value);
  }

  /**
   * Returns nothing if addresses is null.
   * Returns a select with the different addresses when they are set.
   * Returna warning no address could be found if the list is empty.
   */
  function getAddressSelect() {
    if (typeof addressList === 'undefined' || addressList === null) {
      return (null);
    }
    else if (addressList.length === 0) {
      return (
        <Alert variant="danger">
          No addresses could be found for the given postcode. You can enter the
          address manually or try again.
        </Alert>
      )
    }
    else {
      return (
        <div>
          <Form.Select
            id = "addressSelect"
            aria-label = "Select address"
            onChange={handleSelectedAddressChange}
            selected={selectedAddress}
            className="mb-2"
          >
            <option
              value="select"
              key="select"
            >
              Please select your address
            </option>
            {
              addressList.map(
                (value, index) => {
                  return (
                    <option
                      value={ index }
                      key={ index }
                    >
                      { `${value.line1}, ${value.line2}, ${value.town}, ${value.county}` }
                    </option>
                  );
                }
              )
            }
          </Form.Select>
          <Form.Label htmlFor="addressSelect" className="form-label visually-hidden">Select address</Form.Label>
        </div>
      )
    }
  }
  
  if (oldLayout){
  return(
    <div id={id} className={className}>
      <Form key="postcodeSearch" onSubmit={handleAddressSearch}>
        <div className="d-grid gap-2">
          <h3 className="my-2 font-weight-normal text-center">{ formTitle }</h3>
          <div>
            <Form.Label className="form-label visually-hidden" htmlFor="postcode">postcode</Form.Label>
            <InputGroup className="mb-2">
              <Form.Control
                id={postcodeFieldId}
                placeholder="Postcode*"
                type="text"
                autoComplete="postal-code"
                value={postcode}
                onChange={handleFieldChange}
                required
              />
              <LoaderButton
                className = "px-3"
                variant = "primary"
                id = {`${id}_postcodeSearchButton`}
                isLoading = {addressSearchIsLoading}
                type="submit"
                disabled = {!postcode || postcode.length === 0}
              >
                Search
              </LoaderButton>
            </InputGroup>
            { getAddressSelect() }
          </div>
        </div>
      </Form>
      <Form key="addressFields">
        <Form.Control 
          id={addressLine1FieldId}
          className="topinput"
          placeholder="Address Line 1*"
          autoComplete="address-line1"
          type="text"
          value={addressLine1}
          onChange={handleFieldChange}
          required
        />
        <Form.Label htmlFor={addressLine1FieldId} className="form-label visually-hidden">Address Line 1</Form.Label>
        <Form.Control
          id={addressLine2FieldId}
          className="middleinput"
          placeholder="Address Line 2"
          autoComplete="address-line2"
          type="text"
          value={addressLine2}
          onChange={handleFieldChange}
        />
        <Form.Label htmlFor={addressLine2FieldId} className="form-label visually-hidden">Address Line 2</Form.Label>
        <Form.Control 
          id={addressTownFieldId}
          className="middleinput"
          placeholder="Town*"
          autoComplete="address-level1"
          type="text"
          value={addressTown}
          onChange={handleFieldChange}
          required
        />
        <Form.Label htmlFor={addressTownFieldId} className="form-label visually-hidden">Town</Form.Label>
        <Form.Control 
          id={addressCountyFieldId}
          className="middleinput"
          placeholder="County*"
          type="text"
          value={addressCounty}
          onChange={handleFieldChange}
          required
        />
        <Form.Label htmlFor={addressCountyFieldId} className="form-label visually-hidden">County</Form.Label>
        <Form.Control 
          id={addressCountryFieldId}
          className="bottominput"
          placeholder="Country"
          autoComplete="country-name"
          type="text"
          value={addressCountry}
          onChange={handleFieldChange}
        />
        <Form.Label htmlFor={addressCountryFieldId} className="form-label visually-hidden">Country</Form.Label>
      </Form>
    </div>
  )
  }else{
    return(
      <div id={id} className={className}>
      <Form key="postcodeSearch" onSubmit={handleAddressSearch}>
        <div className="d-grid gap-2">
          <h3 className="my-2 font-weight-normal text-center">{ formTitle }</h3>
          <div>
            
            <InputGroup className="mb-2">
              <div class="form-floating">
              <Form.Control
                id={postcodeFieldId}
                placeholder="Postcode*"
                type="text"
                autoComplete="postal-code"
                value={postcode}
                onChange={handleFieldChange}
                required
              />
            <Form.Label className="form-label" htmlFor="postcode">Postcode *</Form.Label>
            </div>  
              <LoaderButton
                className = "px-3"
                variant = "primary"
                id = {`${id}_postcodeSearchButton`}
                isLoading = {addressSearchIsLoading}
                type="submit"
                disabled = {!postcode || postcode.length === 0}
              >
                Search
              </LoaderButton>
            </InputGroup>
            { getAddressSelect() }
          </div>
        </div>
      </Form>
      <Form key="addressFields">
      <div class="form-floating mt-1">
        <Form.Control 
          id={addressLine1FieldId}
          className="input"
          placeholder="Address Line 1*"
          autoComplete="address-line1"
          type="text"
          value={addressLine1}
          onChange={handleFieldChange}
          required
        />
        <Form.Label htmlFor={addressLine1FieldId} className="form-label">Address Line 1 *</Form.Label>
      </div>
      <div class="form-floating mt-1">
        <Form.Control
          id={addressLine2FieldId}
          className="input"
          placeholder="Address Line 2"
          autoComplete="address-line2"
          type="text"
          value={addressLine2}
          onChange={handleFieldChange}
        />
        <Form.Label htmlFor={addressLine2FieldId} className="form-label">Address Line 2</Form.Label>
        </div>
        <div class="form-floating mt-1">
        <Form.Control 
          id={addressTownFieldId}
          className="input"
          placeholder="Town*"
          autoComplete="address-level1"
          type="text"
          value={addressTown}
          onChange={handleFieldChange}
          required
        />
        <Form.Label htmlFor={addressTownFieldId} className="form-label">Town *</Form.Label>
        </div>
        <div class="form-floating mt-1">
        <Form.Control 
          id={addressCountyFieldId}
          className="input"
          placeholder="County*"
          type="text"
          value={addressCounty}
          onChange={handleFieldChange}
          required
        />
        <Form.Label htmlFor={addressCountyFieldId} className="form-label">County *</Form.Label>
        </div>
        <div class="form-floating mt-1">
        <Form.Control 
          id={addressCountryFieldId}
          className="input"
          placeholder="Country"
          autoComplete="country-name"
          type="text"
          value={addressCountry}
          onChange={handleFieldChange}
        />
        <Form.Label htmlFor={addressCountryFieldId} className="form-label">Country</Form.Label>
        </div>
        
      </Form>
    </div>
    )
  }
};

AddressPicker.propTypes = {
  /**
   * Title to display at top of form.
   */
  formTitle: PropTypes.string,
  /**
   * Unique id for the form (used on sub-components to guarantee uniqueness
   * when there are multiple AddressPicker components.)
   */
  id: PropTypes.string,
  /**
   * ClassName to apply to the outer div of the component.
   */
  className: PropTypes.string,
  /**
   * Pass the handleFieldChange function of the containing form.
   */
  handleFieldChange: PropTypes.func,

  /**
   * Postcode passed down from formfields in containing form
   * (e.g. fields.postcode_1).
   */
  postcode: PropTypes.string,
  /**
   * addressLine1 passed down from formfields in containing form
   * (e.g. fields.addressLine1_1).
   */
  addressLine1: PropTypes.string,
  /**
   * addressLine2 passed down from formfields in containing form
   * (e.g. fields.addressLine2_1).
   */
  addressLine2: PropTypes.string,
  /**
   * addressTown passed down from formfields in containing form
   * (e.g. fields.addressTown_1).
   */
  addressTown: PropTypes.string,
  /**
   * addressCounty passed down from formfields in containing form
   * (e.g. fields.addressCounty_1).
   */
  addressCounty: PropTypes.string,
  /**
   * addressCountry passed down from formfields in containing form
   * (e.g. fields.addressCountry_1).
   */
  addressCountry: PropTypes.string,

  /**
   * Id for the postcode field. Should match id in useFormFields
   * data-structure.
   */
  postcodeFieldId: PropTypes.string,
  /**
   * Id for the addressLine1 field. Should match id in useFormFields
   * data-structure.
   */
  addressLine1FieldId: PropTypes.string,
  /**
   * Id for the addressLine2 field. Should match id in useFormFields
   * data-structure.
   */
  addressLine2FieldId: PropTypes.string,
  /**
   * Id for the addressTown field. Should match id in useFormFields
   * data-structure.
   */
  addressTownFieldId: PropTypes.string,
  /**
   * Id for the addressCounty field. Should match id in useFormFields
   * data-structure.
   */
  addressCountyFieldId: PropTypes.string,
  /**
   * Id for the addressCountry field. Should match id in useFormFields
   * data-structure.
   */
  addressCountryFieldId: PropTypes.string,
}

export default AddressPicker;